import React, { useState, useEffect } from 'react';
import styles from './ReusableCustombtn.module.css';
import { ReactComponent as Filter } from '../../assets/SVG/filter.svg';
import { ReactComponent as SearchIcon } from '../../assets/SVG/Search-Icon.svg';
import { Dialog, TextField, Grid2, createTheme, ThemeProvider, Tooltip, IconButton, InputAdornment } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterPopup from '../RegistrationComponent/FilterComponent/index';
import { ButtonType, ReusableCustombtnProps } from '../../types';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../alertProvider';
import { useTranslation } from 'react-i18next';
import { getTodayDefaultDate, getOneMonthAgoDate } from '../../utils/commonUtils';


const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        size: 'small', 
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small', 
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#E17E61',  
          },
        },
      },
    },
  },
});

export default function ReusableCustombtn({
  type,
  buttonName,
  selectedButton,
  radioOptions,
  onRadioChange,
  selectedValue,
  formDate,
  toDate,
  onDateChange,
  inputLabel,
  inputPlaceholder,
  dateFromLabel,
  dateToLabel,
  inputValue,
  onInputChange,
  noMaxDate,
  resetEnabled = false,
  onClick,
  onReset,
}: Readonly<ReusableCustombtnProps>) {
  const navigate = useNavigate();
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  const today = getTodayDefaultDate();
  const defaultStartDate = getOneMonthAgoDate();
  const defaultEndDate = getTodayDefaultDate();

  const [startDate, setStartDate] = useState(formDate || defaultStartDate);
  const [endDate, setEndDate] = useState(toDate || defaultEndDate);
  const [searchValue, setSearchValue] = useState(inputValue || '');
  const [searchFocused, setSearchFocused] = useState(false);
  const { t } = useTranslation('registration_lang');
  const { t: tGeneral } = useTranslation('general');

  // Sync internal state with props when they change (for reset functionality)
  useEffect(() => {
    setStartDate(formDate || defaultStartDate);
  }, [formDate, defaultStartDate]);

  useEffect(() => {
    setEndDate(toDate || defaultEndDate);
  }, [toDate, defaultEndDate]);

  useEffect(() => {
    setSearchValue(inputValue || '');
  }, [inputValue]);

  const {addAlert} = useAlert();

  const routeToNewRegistrationPage = () => {
    if (buttonName === t('Registration.New Registration')) {
      const patientType = selectedValue === 'patient' ? 'PATIENT_COUPLE' : selectedValue ?? 'PATIENT_COUPLE';
      navigate('/admin/registration/patient-partner-registration', {
        state: { selected_patient_type: patientType },
      });
    }
    if (buttonName === t('Registration.Registration')) {
      navigate('/admin/registration/patient-partner-registration');
    }
    if (buttonName === t('Registration.Billing Information')) {
      navigate('/admin/registration/billing-information');
    }
  };

  const handleFilterPopupClose = () => {
    setIsFilterPopupOpen(false);
  };

  const handleFilterPopupOpen = () => {
    setIsFilterPopupOpen(true);
  };

  if (Array.isArray(type) && type.includes('button') && (!buttonName || buttonName.trim() === '')) {
    throw new Error(
      'buttonName must be provided and cannot be an empty string when type includes "button".'
    );
  }

  const types: ButtonType[] = Array.isArray(type) ? type : (type as string).trim().split(' ') as ButtonType[];

  const buttonClass = `btn btn-dark ${styles.addbtn} ${
    buttonName === selectedButton ? styles.selectedButton : ''
  }`;

  const getResetDisplay  = () => resetEnabled && (startDate !== defaultStartDate || endDate !== defaultEndDate);

  const renderElement = (type: ButtonType) => {
    switch (type) {
    case 'button':
      return (
        <button
          type="button"
          className={buttonClass}
          onClick={onClick ? onClick : routeToNewRegistrationPage}
        >
          <span>{buttonName}</span>
        </button>
      );
    case 'toggle':
      return (
        <button
          type='button'
          className={`${styles.togglebutton} ${buttonName === selectedButton ? styles.togglebuttonactive : ''}`}
          onClick={routeToNewRegistrationPage}
        >
          <span className={styles.btnmain}>{buttonName}</span>
        </button>
      );
    case 'filter':
      return (
        <Filter
          className={styles.searchFilter}
          style={{ marginLeft: 10, height: '100%', cursor: 'pointer' }}
          onClick={handleFilterPopupOpen}
        />
      );
    case 'input':
      return (
        <Grid2 size={{xs:12}}>
          <TextField
            sx={{
              width: '100%',
              '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                transform: 'translate(50px, 10px) scale(1)',
              },
            }}
            error={false}
            id="searchInput"
            variant="outlined"
            label={inputLabel ?? tGeneral('Search')}
            placeholder={inputPlaceholder ?? ''}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              onInputChange?.(e.target.value);
            }}
            
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              },
              inputLabel: {
                shrink: searchFocused || searchValue.length > 0,
              },
            }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        </Grid2>
      );
    case 'radio':
      return (
        <div className={styles.radioGroup}>
          {radioOptions?.map(option => (
            <label key={option.value} className={styles.radioLabel}>
              <input
                type="radio"
                name="customRadioGroup"
                value={option.value}
                checked={option.value === selectedValue} 
                onChange={() => {
                  onRadioChange?.(option.value);
                }}
                className={styles.radioInput}
              />
              {option.name}
            </label>
          ))}
        </div>
      );
    case 'date':
      return (
        <div className={styles.dateGroup}>

          
          <Grid2 size={{xs:12, sm: getResetDisplay() ? 5 : 6}}>
            <TextField
              sx={{ width: '100%' }}
              error={false} 
              id="start-date"
              label={dateFromLabel ?? tGeneral('fromDate')}
              type="date"
              variant="outlined"              
              value={startDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setStartDate(newStartDate);
                // Only trigger if both dates are selected and valid
                if (newStartDate && endDate && newStartDate <= endDate) {
                  onDateChange?.(newStartDate, endDate);
                }
              }}             
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  max: noMaxDate ? undefined : today,
                }
              }}
            />
          </Grid2>
          <Grid2 size={{xs:12, sm: getResetDisplay() ? 5 : 6}}>
            <TextField
              sx={{ width: '100%' }}
              error={false} 
              id="end-date"
              label={dateToLabel ?? tGeneral('toDate')}
              type="date"
              variant="outlined"
              value={endDate}
              onChange={(e) => {
                const newEndDate = e.target.value;
                if (newEndDate < startDate) {
                  addAlert(tGeneral('toDateShouldBeGreater'), 'error');                  
                } else {
                  setEndDate(newEndDate);
                  // Only trigger if both dates are selected and valid
                  if (startDate && newEndDate && startDate <= newEndDate) {
                    onDateChange?.(startDate, newEndDate);
                  }
                }
              }}              
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  min: startDate , // disables previous dates
                  max: noMaxDate ? undefined : today, 
                }
              }}
            />
          </Grid2>


          {getResetDisplay() && <Grid2 size={{xs: 'auto'}} sx={{ display: 'flex', alignItems: 'center' }}>            
            <Tooltip title={tGeneral('reset')} arrow>
              <IconButton
                onClick={() => {
                  setStartDate(defaultStartDate);
                  setEndDate(defaultEndDate);
                  onDateChange?.(defaultStartDate, defaultEndDate);
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Grid2>}
        </div>
      );
    case 'reset':
      return (
        <Tooltip title={tGeneral('reset_filters_search_status', 'Reset all filters')} arrow>
          <IconButton
            size="small"
            onClick={onReset}
            sx={{
              ml: 1,
              color: '#000',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.08)',
              },
            }}
          >
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      );
    default:
      return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div>
        <div className={styles.buttonsContainer}>
          {types.map((typeItem) => (
            <React.Fragment key={typeItem}>{renderElement(typeItem)}</React.Fragment>
          ))}
          {isFilterPopupOpen && (
            <Dialog
              open={isFilterPopupOpen}
              onClose={handleFilterPopupClose}
              classes={{ paper: styles.dialogContainer }}
            >
              <FilterPopup onClose={handleFilterPopupClose} />
            </Dialog>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}
